import {
  ALL_CHART_TYPES,
  DEFAULT_NORMALIZE_BY,
  DEFAULT_NORMALIZE_BY_SETTING
} from "constants/chart.constants";
import { CalculationMethod, EvaSource, TimeStep } from "constants/charts.enums";
import { FIELD_NAMES } from "constants/fieldLabels.constants";
import { createMergeWithDefaults } from "entities/utils";
import { InjectorTotalChartRequestType, InjectorTotalChartSettings } from "types";

import { MaxBinsSortOrderEnum } from "models/binSize";
import { EntityKind } from "models/entityKind";
import { PdenSourceEnum } from "models/pdenDataSourceSetting";

import { BASE_CHART_REQUEST } from "./defaults";

// Injector Total is a fully server-driven chart: the X axis is always the monthly On Inj Date
// bucket and the Y axis is always Well Count (calculated via CalculationMethod.Count, mirroring
// how StackedBar computes its "Well Count" bar height — see stackedBar.ts). Neither axis is
// user-editable (no axis-selector UI, see useAxisFieldSelection.tsx, which deliberately omits
// EvaChart.InjectorTotal from both allow-lists), so xVariable/yVariable below are fixed, not
// defaults a user can override via the UI.
export const DEFAULT_INJECTOR_TOTAL_CHART_SETTINGS: Partial<InjectorTotalChartSettings> =
  {
    product: "Oil",
    otherProducts: [],
    chartType: ALL_CHART_TYPES.InjectorTotal.id,
    showForecast: false,
    showWellCount: true,
    pdenDataSourceSetting: {
      source: EvaSource.Public,
      timeStep: TimeStep.Month
    },
    forecastMode: "forecastTrump",
    useForecastStartDate: false,
    survivorBias: true,
    rateType: 0,
    cutoff: 50,
    timeStep: TimeStep.Month,
    shutInMonths: 3,
    source: EvaSource.Public,
    normalizeBy: DEFAULT_NORMALIZE_BY,
    useWeightedRatioAverage: true,
    xVariable: {
      valueProperty: "Dates.OnInj_YearMonth",
      displayTitle: "On Inj Date",
      canBin: false,
      dataType: "Text",
      mappingName: "Dates.OnInj_YearMonth",
      bin: null,
      pdenSource: PdenSourceEnum.Public.toString()
    },
    yVariable: {
      valueProperty: "Well_Design.HzLength_m",
      displayTitle: FIELD_NAMES.HZ_LENGTH,
      canBin: true,
      dataType: "Number",
      mappingName: "Well_Design.HzLength_m",
      pdenSource: PdenSourceEnum.Public.toString()
    },
    lockUnits: false,
    movingAverageDays: null,
    stackedBarCalculation: CalculationMethod.Count
  };

export const DEFAULT_INJECTOR_TOTAL_REQUEST: Partial<InjectorTotalChartRequestType> = {
  ...BASE_CHART_REQUEST,
  FilterId: "",
  requestId: "",
  ReverseColor: false,
  ColorPalette: {
    id: "-6",
    name: "Portland",
    shared: false,
    colors: ["rgb(12,51,131)"],
    preferredColors: [],
    paletteType: 0,
    colorScale: [],
    specialColors: null,
    thickness: [2],
    reverse: false
  },
  GroupBy: {
    title: "Resource Play",
    property: "Header.ResourcePlay",
    groupByField: "Header.ResourcePlay",
    pdenSource: "Public",
    canBin: false,
    dataType: "Text",
    categoryId: 1,
    tooltip: "",
    display: "",
    entityKind: EntityKind.Well,
    bin: {
      BinSize: 0,
      MinSize: null,
      MaxBins: null,
      MaxBinsSortOrder: MaxBinsSortOrderEnum.WellCount,
      GreaterThan: null,
      LessThan: null,
      Quantile: {
        quantileType: "NumberOfQuantile",
        numQuantiles: 4
      },
      BinType: "BinSize",
      UseDynamicBins: false
    },
    mode: undefined,
    forecastTrumpingSettings: undefined,
    performanceDateRange: undefined
  },
  sortBy: "AlphabeticalAsc",
  normalizeTypeWell: true,
  userForecast: [],
  userForecastSettings: {
    forecastSourceFolder: null,
    excludeMcDanielForecasts: false,
    reserveCategory: null,
    excludeNoviForecasts: true,
    specialForecastFolders: ["McDaniel Research"]
  },
  forecastMode: "forecastTrump",
  useForecastStartDate: false,
  showGroupsNotInFilter: true,
  placeholderBinsEnabled: false,
  hasCheckedBins: true,
  syncClientPden: false,
  syncClientSchema: "",
  colorLockedItems: {},
  syncClientFcst: false,
  syncForecastRescat: "2111",
  normalizeBySetting: DEFAULT_NORMALIZE_BY_SETTING,
  chartSetting: DEFAULT_INJECTOR_TOTAL_CHART_SETTINGS as InjectorTotalChartSettings,
  axisMinMax: {
    xMin: null,
    xMax: null,
    yMin: null,
    yMax: null
  },
  isDefaultLockOn: true,
  chartFieldInformation: {
    xField: {
      type: "x",
      title: "On Inj Date (Year-Month)",
      property: "Dates.OnInj_YearMonth",
      pdenSource: PdenSourceEnum.Public,
      folderName: "",
      isForecast: false,
      product: "",
      defaultUnit: "Year-Month",
      performanceDateRange: undefined
    },
    yField: {
      type: "y",
      product: "",
      defaultUnit: "",
      title: FIELD_NAMES.HZ_LENGTH,
      property: "Well_Design.HzLength_m",
      pdenSource: PdenSourceEnum.Public,
      folderName: "",
      isForecast: false,
      performanceDateRange: undefined
    },
    groupByField: {
      type: "groupBy",
      title: "Resource Play",
      property: "Header.ResourcePlay",
      defaultUnit: "",
      product: "",
      isForecast: false,
      folderName: "",
      performanceDateRange: undefined
    }
  }
};

export const generateInjectorTotalRequestBody = createMergeWithDefaults(
  DEFAULT_INJECTOR_TOTAL_REQUEST
);
